import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { GraduationCap, Users, Shield, Clock, BookOpen, CheckCircle } from 'lucide-react';

const HomePage = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <BookOpen className="w-6 h-6" />,
      title: 'أنواع أسئلة متعددة',
      description: 'اختيار من متعدد، صح وخطأ، إجابات قصيرة، ومقالية',
      link: '/teacher/login',
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'حماية متقدمة',
      description: 'نظام مراقبة ذكي لمنع الغش والتأكد من نزاهة الاختبار',
      link: '/teacher/login',
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: 'توقيت تلقائي',
      description: 'مؤقت للاختبار مع تسليم تلقائي عند انتهاء الوقت',
      link: '/student/login',
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: 'تصحيح فوري',
      description: 'نتائج سريعة للأسئلة الموضوعية مع إمكانية التصحيح اليدوي',
      link: '/student/login',
    },
  ];

  return (
    <div className="min-h-screen bg-[#FFFFFF]">
      {/* Header */}
      <header className="bg-[#FFFFFF]/80 border-b border-[#E8DFCF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="Logo" className="w-10 h-10 rounded-lg object-cover border border-[#E8DFCF]" />
              <span className="text-xl font-bold text-[#EF9F27]">المعتمد في اللغة العربية</span>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="outline"
                onClick={() => navigate('/teacher/login')}
                className="border-[#E8DFCF] text-[#EF9F27] hover:bg-[#EF9F27]/10"
                data-testid="teacher-login-nav-btn"
              >
                دخول المعلم
              </Button>
              <Button 
                onClick={() => navigate('/student/login')}
                className="bg-[#EF9F27] hover:bg-[#D88E1F] text-[#854F0B] font-bold"
                data-testid="student-login-nav-btn"
              >
                دخول الطالب
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/student/register')}
                data-testid="student-register-nav-btn"
              >
                تسجيل طالب
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 md:py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="text-right">
              <h1 className="text-4xl sm:text-5xl font-bold text-[#412402] leading-tight mb-6">
                منصة الاختبارات الإلكترونية
                <span className="text-[#EF9F27]"> للغة العربية</span>
              </h1>
              <p className="text-lg text-[#6B4C2A] mb-8 leading-relaxed">
                منصة متكاملة لإنشاء وإدارة اختبارات اللغة العربية بكل سهولة. 
                تدعم أنواع الأسئلة المختلفة مع نظام حماية متقدم لضمان نزاهة الاختبارات.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  size="lg"
                  onClick={() => navigate('/teacher/login')}
                  className="bg-[#EF9F27] hover:bg-[#D88E1F] text-lg px-8"
                  data-testid="hero-teacher-btn"
                >
                  <GraduationCap className="w-5 h-5 ml-2" />
                  أنا معلم
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => navigate('/student/login')}
                  className="text-lg px-8 border-[#EF9F27] text-[#EF9F27] hover:bg-[#EF9F27]/10"
                  data-testid="hero-student-btn"
                >
                  <Users className="w-5 h-5 ml-2" />
                  أنا طالب
                </Button>
              </div>
            </div>
            <div className="hidden md:block">
              <img 
                src="https://images.unsplash.com/photo-1613982102700-e8715e4f472e?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
                alt="Arabic Learning"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-[#FFFFFF]/80">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#412402] mb-4">مميزات المنصة</h2>
            <p className="text-[#6B4C2A] max-w-2xl mx-auto">
              نوفر لك أدوات متكاملة لإنشاء اختبارات احترافية وآمنة
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="border-[#E8DFCF] hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(feature.link)}>
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-[#EF9F27]/15 rounded-xl flex items-center justify-center mx-auto mb-4 text-[#EF9F27]">
                    {feature.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-[#412402] mb-2">{feature.title}</h3>
                  <p className="text-[#6B4C2A] text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#EF9F27]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-[#412402] mb-4">ابدأ الآن</h2>
          <p className="text-amber-100 mb-8 text-lg">
            أنشئ اختبارك الأول في دقائق معدودة
          </p>
          <Button 
            size="lg"
            variant="secondary"
            onClick={() => navigate('/teacher/register')}
            className="bg-[#FFFFFF]/80 text-[#EF9F27] hover:bg-[#EF9F27]/10 text-lg px-8"
            data-testid="cta-register-btn"
          >
            إنشاء حساب معلم جديد
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#FFFFFF] py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-[#EF9F27] rounded-lg flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-[#412402]" />
              </div>
              <span className="text-[#412402] font-semibold"> المعتمد في اللغة العربية</span>
            </div>
            <p className="text-[#412402]/40 text-sm">
              © {new Date().getFullYear()} جميع الحقوق محفوظة
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
